import { OptJobType, OptJobWithQueueItems, TERMINAL_OPT_JOB_STATUSES } from 'api/optJobs/optJobs.api'
import { QueueItem } from 'api/queueItems/queueItems.types'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueueItemsPhaseProgress } from 'ui/components/OptJobPhaseProgress/OptJobMultiPhaseProgress/hooks/useQueueItemsPhaseProgress'
import { OptJobMultiPhaseProgressMatrix } from 'ui/components/OptJobPhaseProgress/OptJobMultiPhaseProgress/OptJobMultiPhaseProgressMatrix'
import { getOptJobLifecycleEnd } from './OptJobLogRow.helper'

type OptJobLogPhaseProgressProps = {
  optJobWithQueueItems: OptJobWithQueueItems
  /** The row's clock, shared so the row and this panel never disagree by a second. */
  now: number
}

type PhaseProgressMatrixProps = {
  queueItems: QueueItem[]
  optJobType: OptJobType
  now: number
  jobHasEnded: boolean
}

function PhaseProgressMatrix({ queueItems, optJobType, now, jobHasEnded }: PhaseProgressMatrixProps): ReactNode {
  const rows = useQueueItemsPhaseProgress(queueItems, optJobType, now, jobHasEnded)

  return (
    <div className="flex flex-col gap-1">
      <OptJobMultiPhaseProgressMatrix rows={rows} tone="dark" />
    </div>
  )
}

/**
 * Lazy-mounted progress content for a single opt job in the Log: the phase matrix
 * for however many queue items the job has. Only mounted when the parent
 * disclosure is expanded (`Collapse unmountOnExit`), so the derivation is lazy.
 */

//TODO
// Correct phases calculations, their names and calculation between the timestamps

export function OptJobLogPhaseProgress({ optJobWithQueueItems, now }: OptJobLogPhaseProgressProps): ReactNode {
  const { t } = useTranslation()
  const queueItems = optJobWithQueueItems.queue_items

  if (queueItems.length === 0) return <div className="text-xs italic ml-8">{t('No steps to show')}</div>

  // Once the job has ended its items stop being written to, so they are measured
  // against the moment it ended rather than a clock that has moved on since.
  const jobHasEnded = TERMINAL_OPT_JOB_STATUSES.has(optJobWithQueueItems.status)
  const jobEndedAtMs = new Date(getOptJobLifecycleEnd(optJobWithQueueItems)).getTime()
  const clock = jobHasEnded && Number.isFinite(jobEndedAtMs) ? jobEndedAtMs : now

  return (
    <PhaseProgressMatrix
      queueItems={queueItems}
      optJobType={optJobWithQueueItems.opt_job_type}
      now={clock}
      jobHasEnded={jobHasEnded}
    />
  )
}
